import * as React from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Badge,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HistoryIcon from '@mui/icons-material/History';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAppDispatch, useAppSelector } from '../store';
import { getAuthState, logout } from '../store/slices/authSlice';
import { getUnreadNotificationsCount, markAsRead } from '../store/slices/notificationSlice';
import { useNavigate } from 'react-router';
import logo from '../assets/images/logo.png';
import Api from '../services/apis/setup';
import { useEffect, useState } from 'react';

const navItems = [
  { label: 'Home', link: '/', icon: <HomeIcon /> },
  { label: 'Dashboard', link: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Service History', link: '/service-history', icon: <HistoryIcon /> },
  { label: 'Add Vehicle', link: '/add-vehicle', icon: <AddCircleIcon /> },
  { label: 'Add Service', link: '/add-service', icon: <AddCircleIcon /> },
  { label: 'Contact Us', link: '/contact', icon: <ContactMailIcon /> },
];

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElNotifications, setAnchorElNotifications] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { user } = useAppSelector(getAuthState);
  const unreadNotificationsCount = useAppSelector(getUnreadNotificationsCount);
  const notifications = useAppSelector((state: any) => state.notifications?.notifications || []);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  const [fetchHeaderNotifications] = Api.useLazyFetchHeaderNotificationsQuery();
  const [markNotificationRead] = Api.useMarkNotificationReadMutation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch notifications
  useEffect(() => {
    const callNotifications = async () => {
      try {
        await fetchHeaderNotifications({ baseURL: '/vehicles/notifications/header' });
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    if (user) callNotifications();
  }, [user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenNotifications = (event: React.MouseEvent<HTMLElement>) => {
    fetchHeaderNotifications({ baseURL: '/vehicles/notifications/header' });
    setAnchorElNotifications(event.currentTarget);
  };

  const handleCloseNotifications = () => {
    setAnchorElNotifications(null);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    setAnchorElUser(null);
  };

  const handleNavigation = (link: string) => {
    navigate(link);
    handleCloseNavMenu();
    setMobileOpen(false);
  };

  // Drawer content for mobile
  const drawer = (
    <Box sx={{ width: 280, pt: 2, bgcolor: 'var(--primary-color)', minHeight: '100%' }}>
      {/* User info in drawer */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
          <Avatar src={user?.picture} alt={user?.name} sx={{ width: 48, height: 48, border: '2px solid rgba(255,255,255,0.5)' }}>
            {user?.name?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'white' }}>
              {user?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {user?.email}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton onClick={() => handleNavigation(item.link)} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} sx={{ '& .MuiTypography-root': { color: 'white' } }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => { handleNavigation('/settings'); handleCloseNavMenu(); }} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
            <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" sx={{ '& .MuiTypography-root': { color: 'white' } }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
            <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" sx={{ '& .MuiTypography-root': { color: 'white' } }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        sx={{
          backgroundColor: scrolled ? 'rgba(6, 33, 65, 0.98)' : 'var(--primary-color)',
          transition: 'all 0.3s ease',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 70 } }}>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <Box
                component="a"
                href="#"
                onClick={(e) => { e.preventDefault(); navigate('/'); }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="Logo"
                  sx={{
                    height: { xs: 36, md: 42 },
                    width: 'auto',
                    borderRadius: 1,
                  }}
                />
              </Box>
            </Box>

            {/* Mobile menu button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            {/* Desktop navigation - centered */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: 0.5 }}>
              {navItems.map((page) => (
                <Button
                  key={page.label}
                  onClick={() => navigate(page.link)}
                  sx={{
                    color: 'white',
                    display: 'block',
                    textTransform: 'capitalize',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    ...(window.location.pathname === page.link && {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    }),
                  }}
                >
                  {page.label}
                </Button>
              ))}
            </Box>

            {/* Spacer */}
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }} />

            {/* Right side icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
              {/* Notification Icon */}
              <Tooltip title="Notifications">
                <IconButton
                  size="large"
                  aria-label="show notifications"
                  color="inherit"
                  onClick={handleOpenNotifications}
                  sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)' } }}
                >
                <Badge
                    badgeContent={unreadNotificationsCount}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        minWidth: 18,
                        height: 18,
                      },
                    }}
                  >
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* Notification Menu */}
              <Menu
                sx={{
                  mt: '45px',
                  '& .MuiPaper-root': {
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  },
                }}
                id="notifications-appbar"
                anchorEl={anchorElNotifications}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorElNotifications)}
                onClose={handleCloseNotifications}
              >
                <Box sx={{ minWidth: 320, maxWidth: 380 }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Notifications
                      {unreadNotificationsCount > 0 && (
                        <Typography component="span" sx={{ ml: 1, color: 'error.main', fontWeight: 600 }}>
                          ({unreadNotificationsCount})
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  {(() => {
                    const unreadNotifications = notifications?.filter((n: any) => !n.read) || [];
                    const recentNotifications = unreadNotifications.slice(0, 5);
                    const hasMore = unreadNotifications.length > 5;

                    return recentNotifications.length > 0 ? (
                      <>
                        {recentNotifications.map((notification: any) => (
                          <MenuItem
                            key={notification.id}
                            onClick={() => {
                              handleCloseNotifications();
                              dispatch(markAsRead(notification.id));
                              markNotificationRead({
                                baseURL: `/vehicles/notifications/${notification.id}/read`,
                                method: 'PATCH',
                                payload: {},
                              });
                              if (notification.vehicleId) {
                                navigate(`/service-history/${notification.vehicleId}`);
                              }
                            }}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              py: 1.5,
                              borderBottom: '1px solid #f5f5f5',
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { backgroundColor: 'grey.50' },
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              {notification.title}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.3, color: 'text.secondary' }}>
                              {notification.message}
                            </Typography>
                            <Typography variant="caption" sx={{ mt: 0.5, color: 'text.disabled' }}>
                              {notification.time}
                            </Typography>
                          </MenuItem>
                        ))}
                        {hasMore && (
                          <MenuItem
                            onClick={() => {
                              handleCloseNotifications();
                              navigate('/notifications');
                            }}
                            sx={{
                              justifyContent: 'center',
                              borderTop: '1px solid #eee',
                              py: 1.5,
                              color: 'primary.main',
                              fontWeight: 500,
                            }}
                          >
                            See all notifications
                          </MenuItem>
                        )}
                      </>
                    ) : (
                      <MenuItem
                        onClick={() => {
                          handleCloseNotifications();
                          navigate('/notifications');
                        }}
                        sx={{ justifyContent: 'center', py: 3 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No new notifications
                        </Typography>
                      </MenuItem>
                    );
                  })()}
                </Box>
              </Menu>

              {/* User Avatar */}
              <Tooltip title="Account">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, ml: 0.5 }}>
                  <Avatar
                    alt={user?.name || 'User'}
                    src={user?.picture}
                    sx={{
                      width: 36,
                      height: 36,
                      border: '2px solid rgba(255,255,255,0.3)',
                      transition: 'border-color 0.2s',
                      '&:hover': { borderColor: 'rgba(255,255,255,0.8)' },
                    }}
                  >
                    {user?.name?.charAt(0)}
                  </Avatar>
                </IconButton>
              </Tooltip>

              {/* User Menu */}
              <Menu
                sx={{
                  mt: '45px',
                  '& .MuiPaper-root': {
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    minWidth: 180,
                  },
                }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {user?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.email}
                  </Typography>
                </Box>
                <MenuItem
                  onClick={() => {
                    navigate('/settings');
                    handleCloseUserMenu();
                  }}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            bgcolor: 'var(--primary-color)',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Toolbar spacer */}
      <Toolbar sx={{ minHeight: { xs: 64, md: 70 } }} />
    </>
  );
}

export default ResponsiveAppBar;
